import React from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { useAuthV2 } from "@/offering/auth_portal_custom_login_provider";
import { Text } from "@/offering/text";
import { Spacer } from "@/offering/spacer";
import { colors } from "@/offering_v2/components/colors";
import { isEmail } from "@/lib/validation/string";

interface ForgotPasswordFormProps {
  onComplete: (email: string) => void;
  onGoBack: () => void;
  initialEmail?: string;
}

export function ForgotPasswordForm({
  onComplete,
  onGoBack,
  initialEmail = "",
}: ForgotPasswordFormProps) {
  const [email, setEmail] = React.useState(initialEmail);
  const [emailError, setEmailError] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const auth0 = useAuthV2();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if ((value && isEmail(value)) || !value) {
      setEmailError("");
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !isEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !isEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      await auth0.forgotPassword(email);
      onComplete(email);
    } catch (err) {
      setError("Failed to send password reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4">
      <Text size="md">
        Please enter the email address associated with your account. We will
        email you a link to reset your password.
      </Text>
      <Spacer size={16} />
      {error && (
        <>
          <Alert variant="danger">{error}</Alert>
          <Spacer size={8} />
        </>
      )}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder="Email"
            required
            isInvalid={!!emailError}
          />
          {emailError && (
            <Form.Control.Feedback type="invalid">
              {emailError}
            </Form.Control.Feedback>
          )}
        </Form.Group>

        <Button
          variant="primary"
          type="submit"
          disabled={isLoading}
          className="w-100 d-flex align-items-center justify-content-center gap-2"
        >
          {isLoading && <Spinner animation="border" size="sm" />}
          {isLoading ? "Sending..." : "Send Reset Link"}
        </Button>

        <Spacer size={24} />

        <div className="text-center">
          <Text size="sm">Remembered your password?</Text>
          <Spacer size={4} />
          <Button
            variant="link"
            onClick={onGoBack}
            className="p-0 text-decoration-none"
            style={{ color: colors.eggPlantCore }}
          >
            Back to Login
          </Button>
        </div>
      </Form>
    </div>
  );
}
